/* eslint-disable react-doctor/js-min-max-loop, react-doctor/query-mutation-missing-invalidation */

import { useDbClient } from "@tanstack/react-db";
import { useMutation, useMutationState } from "@tanstack/react-query";
import { toast } from "@wags/ui/sonner.tsx";
import { usePostHog } from "posthog-js/react";
import { useState } from "react";
import {
    cancelPendingUnfavorite,
    FavoriteUndoCancelledError,
    getSavedListingIdsCollection,
    useSavedListingIdsSet,
    useRetrySavedListingIds,
} from "./saved-listings-collection";
import {
    type FavoriteMutationVariables,
    favoriteMutationVariablesSchema,
} from "./saved-listing.contract";

const FAVORITE_MUTATION_KEY: readonly ["favorite"] = ["favorite"];
const SAVE_COUNT_KEY = "wags_save_count";

const PROMPT_THRESHOLD = 3;
const SIGNIN_PROMPT_SHOWN_KEY = "wags_signin_prompt_shown";
const ANONYMOUS_SAVE_LIMIT = 5;

export function markSignInPromptShown() {
    try {
        localStorage.setItem(SIGNIN_PROMPT_SHOWN_KEY, "1");
    } catch {}
}

export type FavoriteMutationResult = {
    outcome:
        | "saved"
        | "guest-saved"
        | "guest-saved-sign-in-prompt"
        | "unsaved"
        | "save-limit"
        | "failed";
};

export function didSaveListing(result: FavoriteMutationResult | undefined) {
    return (
        result?.outcome === "saved" ||
        result?.outcome === "guest-saved" ||
        result?.outcome === "guest-saved-sign-in-prompt"
    );
}

export function willStartSave({
    isFavorited,
    atSaveLimit,
}: {
    isFavorited: boolean | undefined;
    atSaveLimit: boolean;
}) {
    return isFavorited === false && !atSaveLimit;
}

export function isAnonymousSaveLimitReached({
    isSignedIn,
    isFavorited,
    savedCount,
}: {
    isSignedIn: boolean | undefined;
    isFavorited: boolean | undefined;
    savedCount: number;
}) {
    return (
        isSignedIn === false &&
        isFavorited === false &&
        savedCount >= ANONYMOUS_SAVE_LIMIT
    );
}

type PendingFavoriteMutation = FavoriteMutationVariables & {
    submittedAt: number;
};

type UseFavoriteMutationOptions = {
    listingId: string;
    isSignedIn: boolean | undefined;
};

export function useListingOptimisticFavorite({
    listingId,
    isPending,
    collectionFavorited,
}: {
    listingId: string;
    isPending: boolean;
    collectionFavorited: boolean | undefined;
}) {
    const [optimisticFavorite, setOptimisticFavorite] = useState<
        { listingId: string; isFavorited: boolean } | undefined
    >();
    const optimisticFavorited =
        optimisticFavorite?.listingId === listingId
            ? optimisticFavorite.isFavorited
            : undefined;

    if (
        optimisticFavorite &&
        (optimisticFavorite.listingId !== listingId ||
            shouldReleaseOptimistic({
                optimisticFavorited,
                isPending,
                collectionFavorited,
            }))
    ) {
        setOptimisticFavorite(undefined);
    }

    const setOptimisticFavorited = (
        targetListingId: string,
        isFavorited: boolean | undefined,
    ) => {
        if (targetListingId !== listingId) return;
        setOptimisticFavorite(
            isFavorited === undefined
                ? undefined
                : { listingId: targetListingId, isFavorited },
        );
    };

    return { optimisticFavorited, setOptimisticFavorited };
}

function incrementSaveCount(): number {
    try {
        const count =
            parseInt(localStorage.getItem(SAVE_COUNT_KEY) ?? "0", 10) + 1;
        localStorage.setItem(SAVE_COUNT_KEY, String(count));
        return count;
    } catch {
        return -1;
    }
}

function getLatestPendingMutation(
    pendingMutations: Array<PendingFavoriteMutation | null>,
    listingId: string,
) {
    return pendingMutations
        .filter((mutation): mutation is PendingFavoriteMutation => {
            if (!mutation) {
                return false;
            }

            return mutation.listingId === listingId;
        })
        .sort((a, b) => b.submittedAt - a.submittedAt)[0];
}

export function resolveIsFavorited({
    optimisticFavorited,
    pendingFavorited,
    collectionFavorited,
}: {
    optimisticFavorited: boolean | undefined;
    pendingFavorited: boolean | undefined;
    collectionFavorited: boolean | undefined;
}): boolean | undefined {
    return optimisticFavorited ?? pendingFavorited ?? collectionFavorited;
}

export function shouldReleaseOptimistic({
    optimisticFavorited,
    isPending,
    collectionFavorited,
}: {
    optimisticFavorited: boolean | undefined;
    isPending: boolean;
    collectionFavorited: boolean | undefined;
}): boolean {
    return (
        optimisticFavorited !== undefined &&
        !isPending &&
        collectionFavorited === optimisticFavorited
    );
}

export function useFavoriteMutation({
    listingId,
    isSignedIn,
}: UseFavoriteMutationOptions) {
    const dbClient = useDbClient();
    const savedListingIdsCollection = getSavedListingIdsCollection(dbClient);
    const signedIn = Boolean(isSignedIn);
    const posthog = usePostHog();
    const savedListings = useSavedListingIdsSet();
    const savedListingIds = savedListings.data;
    const collectionFavorited = savedListings.state?.has(listingId);
    const hasLoadError =
        savedListings.sourceStatus === "error" || savedListings.isError;
    const retryLoad = useRetrySavedListingIds();
    const pendingMutations = useMutationState<PendingFavoriteMutation | null>({
        filters: {
            mutationKey: [...FAVORITE_MUTATION_KEY],
            status: "pending",
        },
        select: (mutation) => {
            const result = favoriteMutationVariablesSchema.safeParse(
                mutation.state.variables,
            );
            if (!result.success) {
                return null;
            }

            return {
                ...result.data,
                submittedAt: mutation.state.submittedAt,
            };
        },
    });

    const pendingMutation = getLatestPendingMutation(
        pendingMutations,
        listingId,
    );
    const mutationIsPending = Boolean(pendingMutation);
    const hasPendingUnsave = pendingMutation?.nextFavorited === false;
    const pendingFavorited = mutationIsPending
        ? pendingMutation?.nextFavorited
        : undefined;

    const { optimisticFavorited, setOptimisticFavorited } =
        useListingOptimisticFavorite({
            listingId,
            isPending: mutationIsPending,
            collectionFavorited,
        });

    const isFavorited = resolveIsFavorited({
        optimisticFavorited,
        pendingFavorited,
        collectionFavorited,
    });

    const isReady = collectionFavorited !== undefined;
    const isPending = mutationIsPending || !isReady;

    const atSaveLimit = isAnonymousSaveLimitReached({
        isSignedIn,
        isFavorited,
        savedCount: savedListingIds?.length ?? 0,
    });

    const mutation = useMutation<
        FavoriteMutationResult,
        Error,
        FavoriteMutationVariables
    >({
        mutationKey: [...FAVORITE_MUTATION_KEY],
        mutationFn: async ({ listingId, nextFavorited, isSignedIn }) => {
            if (nextFavorited) {
                const transaction = savedListingIdsCollection.insert({
                    listingId,
                    savedAt: new Date(),
                });
                await transaction.isPersisted.promise;

                if (!isSignedIn) {
                    const count = incrementSaveCount();
                    if (
                        count >= PROMPT_THRESHOLD &&
                        (() => {
                            try {
                                return (
                                    localStorage.getItem(
                                        SIGNIN_PROMPT_SHOWN_KEY,
                                    ) !== "1"
                                );
                            } catch {
                                return false;
                            }
                        })()
                    ) {
                        return { outcome: "guest-saved-sign-in-prompt" };
                    }
                }

                return {
                    outcome: isSignedIn ? "saved" : "guest-saved",
                };
            }

            if (!savedListingIdsCollection.has(listingId)) {
                await savedListingIdsCollection.utils.refetch({
                    throwOnError: true,
                });
            }

            if (!savedListingIdsCollection.has(listingId)) {
                throw new Error("Saved listing not found");
            }

            const transaction = savedListingIdsCollection.delete(listingId);
            await transaction.isPersisted.promise;

            return { outcome: "unsaved" };
        },
        onError: (error, variables) => {
            if (error instanceof FavoriteUndoCancelledError) {
                return;
            }

            setOptimisticFavorited(variables.listingId, undefined);
            toast.error(
                variables.nextFavorited
                    ? "Couldn't save this dog. Try again."
                    : "Couldn't remove this dog. Try again.",
            );
        },
    });

    const cancelUnsave = () => {
        if (!cancelPendingUnfavorite(dbClient, listingId)) {
            return false;
        }

        setOptimisticFavorited(listingId, true);
        return true;
    };

    const toggle = async (): Promise<FavoriteMutationResult> => {
        if (hasPendingUnsave) {
            const restored = cancelPendingUnfavorite(dbClient, listingId);
            if (!restored) {
                return { outcome: "failed" };
            }

            setOptimisticFavorited(listingId, true);
            posthog.capture("listing_saved", { listingId });
            return {
                outcome: signedIn ? "saved" : "guest-saved",
            };
        }

        if (isFavorited === undefined) {
            return { outcome: "failed" };
        }

        const nextFavorited = !isFavorited;

        if (
            nextFavorited &&
            isAnonymousSaveLimitReached({
                isSignedIn,
                isFavorited,
                savedCount: savedListingIds?.length ?? 0,
            })
        ) {
            const total = savedListingIds?.length ?? 0;
            posthog.capture("save_limit_reached", {
                listingId,
                saved_count: total,
            });
            return { outcome: "save-limit" };
        }

        setOptimisticFavorited(listingId, nextFavorited);

        posthog.capture(nextFavorited ? "listing_saved" : "listing_unsaved", {
            listingId,
        });

        const variables = {
            listingId,
            nextFavorited,
            isSignedIn: signedIn,
        };

        if (!nextFavorited) {
            mutation.mutate(variables);
            return { outcome: "unsaved" };
        }

        try {
            return await mutation.mutateAsync(variables);
        } catch {
            return { outcome: "failed" };
        }
    };

    return {
        cancelUnsave,
        isFavorited,
        isPending,
        isReady,
        retryLoad: hasLoadError ? retryLoad : undefined,
        toggle,
        atSaveLimit,
    };
}
